import React, { useState, useEffect } from 'react';
import '../../SupplierPayments.css';
import supplierPaymentService from '../../../services/supplierPaymentService';
import supplierService from '../../../services/supplierService';

const SupplierPayments = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [supplierPaymentSummary, setSupplierPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  // Search and Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('payment_date');
  const [sortOrder, setSortOrder] = useState('desc');

  const [formData, setFormData] = useState({
    supplier_id: '',
    purchase_order_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: '',
    status: 'completed'
  });

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
  }, []);

  // Apply filters and search whenever relevant states change
  useEffect(() => {
    applyFiltersAndSearch();
  }, [payments, searchTerm, statusFilter, supplierFilter, paymentMethodFilter, dateFilter, sortBy, sortOrder]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await supplierPaymentService.getAllPayments();
      setPayments(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch payments');
      console.error('Error fetching payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierService.getActiveSuppliers();
      setSuppliers(response || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const fetchSupplierPurchaseOrders = async (supplierId) => {
    try {
      const response = await supplierPaymentService.getSupplierPurchaseOrders(supplierId);
      setPurchaseOrders(response || []);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
    }
  };

  const fetchPurchaseOrderPaymentSummary = async (purchaseOrderId) => {
    if (!purchaseOrderId) {
      setPaymentSummary(null);
      return;
    }
    
    setSummaryLoading(true);
    try {
      const response = await supplierPaymentService.getPurchaseOrderPaymentSummary(purchaseOrderId);
      setPaymentSummary(response);
      setSelectedPurchaseOrder(response.purchase_order);
    } catch (err) {
      console.error('Error fetching payment summary:', err);
      setPaymentSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchSupplierPaymentSummary = async (supplierId) => {
    if (!supplierId) {
      setSupplierPaymentSummary(null);
      return;
    }
    
    try {
      const response = await supplierPaymentService.getSupplierPaymentSummary(supplierId);
      setSupplierPaymentSummary(response);
    } catch (err) {
      console.error('Error fetching supplier payment summary:', err);
      setSupplierPaymentSummary(null);
    }
  };

  const applyFiltersAndSearch = () => {
    let result = [...payments];

    // Apply search filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(payment =>
        payment.reference_number?.toLowerCase().includes(lowercasedSearch) ||
        payment.supplier?.name?.toLowerCase().includes(lowercasedSearch) ||
        payment.purchase_order?.po_number?.toLowerCase().includes(lowercasedSearch) ||
        payment.notes?.toLowerCase().includes(lowercasedSearch) ||
        payment.payment_method?.toLowerCase().includes(lowercasedSearch)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(payment => payment.status === statusFilter);
    }

    // Apply supplier filter
    if (supplierFilter !== 'all') {
      result = result.filter(payment => payment.supplier_id == supplierFilter);
    }

    // Apply payment method filter
    if (paymentMethodFilter !== 'all') {
      result = result.filter(payment => payment.payment_method === paymentMethodFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0));
      const endOfDay = new Date(today.setHours(23, 59, 59, 999));

      switch (dateFilter) {
        case 'today':
          result = result.filter(payment => {
            const paymentDate = new Date(payment.payment_date);
            return paymentDate >= startOfDay && paymentDate <= endOfDay;
          });
          break;
        case 'this_week':
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          result = result.filter(payment => {
            const paymentDate = new Date(payment.payment_date);
            return paymentDate >= startOfWeek && paymentDate <= endOfDay;
          });
          break;
        case 'this_month':
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          result = result.filter(payment => {
            const paymentDate = new Date(payment.payment_date);
            return paymentDate >= startOfMonth && paymentDate <= endOfDay;
          });
          break;
        case 'last_30_days':
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          thirtyDaysAgo.setHours(0, 0, 0, 0);
          result = result.filter(payment => {
            const paymentDate = new Date(payment.payment_date);
            return paymentDate >= thirtyDaysAgo && paymentDate <= endOfDay;
          });
          break;
        default:
          break;
      }
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';

      // Handle nested properties
      if (sortBy === 'supplier.name') {
        aValue = a.supplier?.name || '';
        bValue = b.supplier?.name || '';
      } else if (sortBy === 'purchase_order.po_number') {
        aValue = a.purchase_order?.po_number || '';
        bValue = b.purchase_order?.po_number || '';
      }

      // Handle date comparison
      if (sortBy.includes('date')) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle numeric comparison
      if (sortBy === 'amount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredPayments(result);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSupplierFilter = (e) => {
    setSupplierFilter(e.target.value);
  };

  const handlePaymentMethodFilter = (e) => {
    setPaymentMethodFilter(e.target.value);
  };

  const handleDateFilter = (e) => {
    setDateFilter(e.target.value);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSupplierFilter('all');
    setPaymentMethodFilter('all');
    setDateFilter('all');
    setSortBy('payment_date');
    setSortOrder('desc');
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleSupplierChange = (supplierId) => {
    setFormData({
      ...formData,
      supplier_id: supplierId,
      purchase_order_id: '',
      amount: ''
    });
    
    setPurchaseOrders([]);
    setPaymentSummary(null);
    setSelectedPurchaseOrder(null);
    
    if (supplierId) {
      fetchSupplierPurchaseOrders(supplierId);
      fetchSupplierPaymentSummary(supplierId);
    }
  };

  const handlePurchaseOrderChange = (purchaseOrderId) => {
    setFormData({
      ...formData,
      purchase_order_id: purchaseOrderId
    });
    
    fetchPurchaseOrderPaymentSummary(purchaseOrderId);
  };

  const handleAmountChange = (amount) => {
    setFormData({
      ...formData,
      amount: parseFloat(amount) || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingPayment) {
        await supplierPaymentService.updatePayment(editingPayment.id, formData);
      } else {
        await supplierPaymentService.createPayment(formData);
      }
      
      setShowModal(false);
      setEditingPayment(null);
      resetForm();
      fetchPayments();
    } catch (err) {
      setError('Failed to save payment');
      console.error('Error saving payment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      supplier_id: payment.supplier_id,
      purchase_order_id: payment.purchase_order_id || '',
      payment_date: payment.payment_date.split('T')[0],
      amount: payment.amount,
      payment_method: payment.payment_method,
      reference_number: payment.reference_number,
      notes: payment.notes,
      status: payment.status
    });
    
    // Fetch related data
    if (payment.supplier_id) {
      fetchSupplierPurchaseOrders(payment.supplier_id);
      if (payment.purchase_order_id) {
        fetchPurchaseOrderPaymentSummary(payment.purchase_order_id);
      }
    }
    
    setShowModal(true);
  };

  const handleDelete = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await supplierPaymentService.deletePayment(paymentId);
        fetchPayments();
      } catch (err) {
        setError('Failed to delete payment');
        console.error('Error deleting payment:', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      purchase_order_id: '',
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'bank_transfer',
      reference_number: '',
      notes: '',
      status: 'completed'
    });
    setEditingPayment(null);
    setPurchaseOrders([]);
    setPaymentSummary(null);
    setSelectedPurchaseOrder(null);
    setSupplierPaymentSummary(null);
  };

  const showSupplierSummary = async (supplierId) => {
    if (supplierId) {
      await fetchSupplierPaymentSummary(supplierId);
      setShowSummaryModal(true);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      completed: 'status-completed',
      pending: 'status-pending',
      failed: 'status-failed'
    };
    
    return `status-badge ${statusClasses[status] || 'status-pending'}`;
  };

  const getPaymentMethodBadge = (method) => {
    const methodLabels = {
      cash: 'Cash',
      bank_transfer: 'Bank Transfer',
      check: 'Check',
      credit_card: 'Credit Card'
    };
    
    return <span className="payment-method">{methodLabels[method] || method}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR'
    }).format(amount || 0);
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || paymentMethodFilter !== 'all' || dateFilter !== 'all';

  return (
    <div className="supplier-payments-container">
      <div className="supplier-payments-header">
        <div>
          <h1>Supplier Payments</h1>
          <p>Manage payments to suppliers with purchase order tracking</p>
        </div>
        <div className="supplier-payments-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            Record Payment
          </button>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="supplier-payments-controls">
        <div className="supplier-payments-search-filter">
          <div className="supplier-payments-search-box">
            <input
              type="text"
              placeholder="Search reference, supplier, PO number, notes..."
              value={searchTerm}
              onChange={handleSearch}
              className="supplier-payments-search-input"
            />
            <span className="supplier-payments-search-icon">🔍</span>
          </div>
          
          <div className="supplier-payments-filter-group">
            <select 
              value={statusFilter} 
              onChange={handleStatusFilter}
              className="supplier-payments-filter-select"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>

            <select 
              value={supplierFilter} 
              onChange={handleSupplierFilter}
              className="supplier-payments-filter-select"
            >
              <option value="all">All Suppliers</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>

            <select 
              value={paymentMethodFilter} 
              onChange={handlePaymentMethodFilter}
              className="supplier-payments-filter-select"
            >
              <option value="all">All Methods</option>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="check">Check</option>
              <option value="credit_card">Credit Card</option>
            </select>

            <select 
              value={dateFilter} 
              onChange={handleDateFilter}
              className="supplier-payments-filter-select"
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="last_30_days">Last 30 Days</option>
            </select>

            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="supplier-payments-filter-select"
            >
              <option value="payment_date">Sort by Date</option>
              <option value="amount">Sort by Amount</option>
              <option value="supplier.name">Sort by Supplier</option>
              <option value="status">Sort by Status</option>
            </select>

            <button 
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="supplier-payments-sort-btn"
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>

            {hasActiveFilters && (
              <button 
                onClick={clearFilters}
                className="supplier-payments-clear-filters-btn"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="supplier-payments-results-info">
          Showing {filteredPayments.length} of {payments.length} payments
          {searchTerm && (
            <span className="supplier-payments-search-term">
              for "{searchTerm}"
            </span>
          )}
        </div>
      </div>

      <div className="page-content">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {loading && !payments.length ? (
          <div className="loading">Loading payments...</div>
        ) : (
          <div className="supplier-payments-table-container">
            {filteredPayments.length === 0 ? (
              <div className="supplier-payments-empty-state">
                <div className="supplier-payments-empty-icon">💸</div>
                <h3>No payments found</h3>
                <p>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filters to find what you\'re looking for.'
                    : 'No payments recorded yet. Record your first payment to get started.'
                  }
                </p>
                {hasActiveFilters && (
                  <button 
                    onClick={clearFilters}
                    className="btn btn-primary"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <table className="supplier-payments-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('reference_number')} className="supplier-payments-sortable-header">
                      Reference {getSortIcon('reference_number')}
                    </th>
                    <th onClick={() => handleSort('supplier.name')} className="supplier-payments-sortable-header">
                      Supplier {getSortIcon('supplier.name')}
                    </th>
                    <th onClick={() => handleSort('purchase_order.po_number')} className="supplier-payments-sortable-header">
                      PO Number {getSortIcon('purchase_order.po_number')}
                    </th>
                    <th onClick={() => handleSort('payment_date')} className="supplier-payments-sortable-header">
                      Payment Date {getSortIcon('payment_date')}
                    </th>
                    <th onClick={() => handleSort('amount')} className="supplier-payments-sortable-header">
                      Amount {getSortIcon('amount')}
                    </th>
                    <th>Payment Method</th>
                    <th onClick={() => handleSort('status')} className="supplier-payments-sortable-header">
                      Status {getSortIcon('status')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map(payment => (
                    <tr key={payment.id}>
                      <td>{payment.reference_number || 'N/A'}</td>
                      <td>
                        <div className="supplier-with-summary">
                          {payment.supplier?.name}
                          <button 
                            className="btn btn-sm btn-info summary-btn"
                            onClick={() => showSupplierSummary(payment.supplier_id)}
                            title="View Supplier Payment Summary"
                          >
                            📊
                          </button>
                        </div>
                      </td>
                      <td>{payment.purchase_order?.po_number || 'N/A'}</td>
                      <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                      <td className="amount amount-positive">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td>{getPaymentMethodBadge(payment.payment_method)}</td>
                      <td>
                        <span className={getStatusBadge(payment.status)}>
                          {payment.status}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn btn-sm btn-info"
                            onClick={() => handleEdit(payment)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(payment.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Add/Edit Payment Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal large-modal">
              <div className="modal-header">
                <h3>{editingPayment ? 'Edit Payment' : 'Record New Payment'}</h3>
                <button className="close-btn" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Supplier *</label>
                    <select
                      value={formData.supplier_id}
                      onChange={(e) => handleSupplierChange(e.target.value)}
                      required
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {supplierPaymentSummary && (
                    <div className="summary-card">
                      <h4>Supplier Payment Summary</h4>
                      <div className="summary-grid">
                        <div className="summary-item">
                          <label>Total Purchases:</label>
                          <span className="amount">{formatCurrency(supplierPaymentSummary.payment_summary?.total_purchase_amount)}</span>
                        </div>
                        <div className="summary-item">
                          <label>Total Paid:</label>
                          <span className="amount positive">{formatCurrency(supplierPaymentSummary.payment_summary?.total_paid_amount)}</span>
                        </div>
                        <div className="summary-item">
                          <label>Remaining:</label>
                          <span className={`amount ${supplierPaymentSummary.payment_summary?.remaining_amount > 0 ? 'negative' : 'positive'}`}>
                            {formatCurrency(supplierPaymentSummary.payment_summary?.remaining_amount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Purchase Order (Optional)</label>
                    <select
                      value={formData.purchase_order_id}
                      onChange={(e) => handlePurchaseOrderChange(e.target.value)}
                    >
                      <option value="">Select Purchase Order (Optional)</option>
                      {purchaseOrders.map(order => (
                        <option key={order.id} value={order.id}>
                          {order.po_number} - {formatCurrency(order.total_amount)} - {order.status}
                        </option>
                      ))}
                    </select>
                  </div>

                  {paymentSummary && (
                    <div className="summary-card">
                      <h4>Purchase Order Payment Summary</h4>
                      <div className="summary-grid">
                        <div className="summary-item">
                          <label>PO Total:</label>
                          <span className="amount">{formatCurrency(paymentSummary.total_amount)}</span>
                        </div>
                        <div className="summary-item">
                          <label>Already Paid:</label>
                          <span className="amount positive">{formatCurrency(paymentSummary.total_paid_amount)}</span>
                        </div>
                        <div className="summary-item">
                          <label>Remaining:</label>
                          <span className={`amount ${paymentSummary.remaining_amount > 0 ? 'negative' : 'positive'}`}>
                            {formatCurrency(paymentSummary.remaining_amount)}
                          </span>
                        </div>
                        <div className="summary-item full-width">
                          <label>Payment Progress:</label>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill"
                              style={{ width: `${paymentSummary.payment_progress}%` }}
                            ></div>
                          </div>
                          <span className="progress-text">{paymentSummary.payment_progress.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Payment Date *</label>
                      <input
                        type="date"
                        value={formData.payment_date}
                        onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Amount *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => handleAmountChange(e.target.value)}
                        required
                        placeholder="Enter payment amount"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Payment Method *</label>
                      <select
                        value={formData.payment_method}
                        onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                        required
                      >
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="check">Check</option>
                        <option value="credit_card">Credit Card</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Status *</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        required
                      >
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Reference Number</label>
                    <input
                      type="text"
                      value={formData.reference_number}
                      onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                      placeholder="e.g., REF-20240115-001"
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows="3"
                      placeholder="Additional notes about this payment..."
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : (editingPayment ? 'Update' : 'Record')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Supplier Payment Summary Modal */}
        {showSummaryModal && supplierPaymentSummary && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Payment Summary - {supplierPaymentSummary.supplier?.name}</h3>
                <button className="close-btn" onClick={() => setShowSummaryModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="summary-card large">
                  <div className="summary-grid">
                    <div className="summary-item">
                      <label>Total Purchases:</label>
                      <span className="amount total">{formatCurrency(supplierPaymentSummary.payment_summary?.total_purchase_amount)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Total Paid:</label>
                      <span className="amount positive">{formatCurrency(supplierPaymentSummary.payment_summary?.total_paid_amount)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Remaining Balance:</label>
                      <span className={`amount ${supplierPaymentSummary.payment_summary?.remaining_amount > 0 ? 'negative' : 'positive'}`}>
                        {formatCurrency(supplierPaymentSummary.payment_summary?.remaining_amount)}
                      </span>
                    </div>
                  </div>
                  
                  {supplierPaymentSummary.payment_summary?.total_purchase_amount > 0 && (
                    <div className="progress-section">
                      <label>Overall Payment Progress:</label>
                      <div className="progress-bar large">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${(supplierPaymentSummary.payment_summary.total_paid_amount / supplierPaymentSummary.payment_summary.total_purchase_amount) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {((supplierPaymentSummary.payment_summary.total_paid_amount / supplierPaymentSummary.payment_summary.total_purchase_amount) * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowSummaryModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierPayments;